import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { MdContentCopy } from "react-icons/md";
import { toast } from 'react-toastify';

// Import CSS files
import "../../Dashboard Style/core.css";
import "../../Dashboard Style/flatpickr.min.css";
import "../../assets1/css/custom.css";
import "../../assets1/css/style.css";

import Sidebar from '../Core/Sidebar';
import Navbar from '../Core/Navbar';
import { logout } from "../../Redux/user_information";

const RegisterPaymentList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id: transactionId } = useParams();
  const location = useLocation();
  const authToken = useSelector((state) => state.user?.token);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [paymentList, setPaymentList] = useState([]);
  const [copiedId, setCopiedId] = useState(null);
  const token = useSelector((state) => state.user?.token);

  const [isMobile, setIsMobile] = useState(false);

  // Check screen size on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 992;
      setIsMobile(mobile);

      // Auto-close sidebar on mobile by default
      if (mobile) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(true);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);



  const closeSidebar = () => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  };


  // 从location.state获取直接传递的数据
  const [directData] = useState(() => {
    if (location.state && location.state.paymentData) {
      return location.state.paymentData;
    }
    return null;
  });

  // 标记是否正在使用直接数据
  const [usingDirectData, setUsingDirectData] = useState(!!directData);

  // Add toggle sidebar function
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Enhanced copy function with tooltip feedback
  const copyToClipboard = (text, fieldName = "text", id = null) => {
    if (!text) {
      toast.warning(`No ${fieldName} to copy`);
      return;
    }

    navigator.clipboard.writeText(text)
      .then(() => {
        if (id) {
          setCopiedId(id);
          setTimeout(() => setCopiedId(null), 2000);
        }
        toast.success(`${fieldName} copied to clipboard!`);
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
        toast.error("Failed to copy to clipboard");
      });
  };

  // Format text for display
  const formatText = (text, maxLength = 20) => {
    if (!text) return 'N/A';
    if (text.length <= maxLength) return text;
    return `${text.substring(0, maxLength)}...`;
  };

  // Function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  };

  // Function to get status badge class
  const getStatusBadgeClass = (status) => {
    if (!status) return 'bg-secondary';

    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case 'approved':
      case 'completed':
      case 'success':
        return 'bg-success';
      case 'pending':
      case 'processing':
        return 'bg-warning text-dark';
      case 'rejected':
      case 'failed':
      case 'cancelled':
        return 'bg-danger';
      default:
        return 'bg-secondary';
    }
  };

  // Get payment mode badge color
  const getPaymentModeBadge = (mode) => {
    if (!mode) return 'bg-secondary';

    const modeLower = mode.toLowerCase();
    if (modeLower.includes('usdt')) return 'bg-success';
    if (modeLower.includes('trc20')) return 'bg-warning text-dark';
    if (modeLower.includes('bep20')) return 'bg-info';
    if (modeLower.includes('erc20')) return 'bg-primary';
    return 'bg-secondary';
  };

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .sidebar-body {
        height: calc(100vh - 70px);
        overflow-y: auto;
      }
      
      .sidebar-body::-webkit-scrollbar {
        width: 6px;
      }
      
      .sidebar-body::-webkit-scrollbar-track {
        background: rgba(255, 255, 255, 0.05);
        border-radius: 10px;
      }
      
      .sidebar-body::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.2);
        border-radius: 10px;
      }
      
      .sidebar-body::-webkit-scrollbar-thumb:hover {
        background: rgba(255, 255, 255, 0.3);
      }
      
      .sidebar .nav-dropdown {
        list-style: none;
        padding-left: 0;
        max-height: 0;
        overflow: hidden;
        transition: max-height 0.3s ease;
      }
      
      .sidebar .nav-dropdown.show {
        max-height: 500px;
      }
      
      .sidebar .nav-dropdown-item {
        padding-left: 2.5rem;
      }
      
      .nav-item.has-dropdown .nav-link {
        position: relative;
        padding-right: 2.5rem;
      }
      
      .nav-item.has-dropdown .dropdown-icon {
        position: absolute;
        right: 1rem;
        top: 50%;
        transform: translateY(-50%);
        transition: transform 0.3s ease;
      }
      
      .nav-item.has-dropdown .nav-link[aria-expanded="true"] .dropdown-icon {
        transform: translateY(-50%) rotate(180deg);
      }
      
      .nav-item.has-dropdown .nav-dropdown {
        background: rgba(255, 255, 255, 0.05);
        border-left: 3px solid var(--primary-color, #0d6efd);
        margin-left: 1rem;
        border-radius: 0 0 8px 8px;
      }
      
      .nav-item.has-dropdown .nav-dropdown-item .nav-link {
        padding: 0.75rem 1rem 0.75rem 2rem;
        color: rgba(255, 255, 255, 0.8);
      }
      
      .nav-item.has-dropdown .nav-dropdown-item .nav-link:hover {
        background: rgba(255, 255, 255, 0.1);
        color: white;
      }
      
      .nav-item.has-dropdown .link-title.nav-item.nav-category {
        font-weight: 600;
        font-size: 0.875rem;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }
      
      .sidebar {
        transition: all 0.3s ease;
      }
      
      /* Direct data banner styles */
      .direct-data-banner {
        background: linear-gradient(135deg, rgba(40, 167, 69, 0.15) 0%, rgba(40, 167, 69, 0.05) 100%);
        border-left: 4px solid #28a745;
        padding: 12px 16px;
        border-radius: 8px;
        margin-bottom: 20px;
        animation: slideIn 0.5s ease;
      }
      
      @keyframes slideIn {
        from {
          opacity: 0;
          transform: translateY(-10px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Initialize sidebar functionality
  useEffect(() => {
    const sidebar = document.querySelector('.sidebar');
    const sidebarBody = document.querySelector('.sidebar-body');

    if (sidebar) {
      if (sidebarOpen) {
        sidebar.classList.remove('close');
      } else {
        sidebar.classList.add('close');
      }
    }

    if (sidebarBody) {
      sidebarBody.style.height = 'calc(100vh - 70px)';
      sidebarBody.style.overflowY = 'auto';
    }

    if (directData) {
      console.log("Using direct data:", directData);

      setPaymentList([directData]);
      setUsingDirectData(true);
      setIsLoading(false);
      return;
    }

    // 否则根据URL参数调用API
    if (transactionId) {
      fetchRegisterPaymentList(transactionId);
      setUsingDirectData(false);
    }

    // Add custom styles
    const style = document.createElement('style');
    style.innerHTML = `
      /* Custom scrollbar for sidebar */
      .sidebar-body {
        height: calc(100vh - 70px);
        overflow-y: auto;
      }
      
      .sidebar-body::-webkit-scrollbar {
        width: 6px;
      }
      
      .sidebar-body::-webkit-scrollbar-track {
        background: rgba(255, 255, 255, 0.05);
        border-radius: 10px;
      }
      
      .sidebar-body::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.2);
        border-radius: 10px;
      }
      
      .sidebar-body::-webkit-scrollbar-thumb:hover {
        background: rgba(255, 255, 255, 0.3);
      }
      
      /* Payment report specific styles */
      .register-payment-card {
        background: rgba(30, 30, 40, 0.9);
        border-radius: 12px;
        border: 1px solid rgba(255, 255, 255, 0.1);
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
      }
      
      .register-payment-table thead th {
        background: linear-gradient(135deg, rgba(13, 110, 253, 0.2) 0%, rgba(102, 126, 234, 0.2) 100%);
        color: #ffffff;
        font-weight: 700;
        text-transform: uppercase;
        font-size: 13px;
        letter-spacing: 0.5px;
        padding: 16px 12px;
        border-bottom: 2px solid rgba(13, 110, 253, 0.3);
      }
      
      .register-payment-table tbody td {
        padding: 14px 12px;
        vertical-align: middle;
        border-bottom: 1px solid rgba(255, 255, 255, 0.05);
        color: #e9ecef;
        background: rgba(40, 40, 50, 0.5);
      }
      
      .register-payment-table tbody tr:nth-child(even) td {
        background: rgba(35, 35, 45, 0.5);
      }
      
      .register-payment-table tbody tr:hover td {
        background: rgba(50, 50, 65, 0.7);
      }
      
      /* Copy button styles */
      .copy-btn {
        transition: all 0.2s ease;
        opacity: 0.7;
      }
      
      .copy-btn:hover {
        opacity: 1;
        transform: scale(1.1);
      }
      
      /* Tooltip for copy feedback */
      .copy-tooltip {
        position: absolute;
        top: -30px;
        left: 50%;
        transform: translateX(-50%);
        background: #28a745;
        color: white;
        padding: 4px 8px;
        border-radius: 4px;
        font-size: 12px;
        white-space: nowrap;
        z-index: 1000;
        animation: fadeInOut 2s ease;
      }
      
      .copy-tooltip:after {
        content: '';
        position: absolute;
        bottom: -5px;
        left: 50%;
        transform: translateX(-50%);
        border-left: 5px solid transparent;
        border-right: 5px solid transparent;
        border-top: 5px solid #28a745;
      }
      
      @keyframes fadeInOut {
        0% { opacity: 0; transform: translateX(-50%) translateY(5px); }
        20% { opacity: 1; transform: translateX(-50%) translateY(0); }
        80% { opacity: 1; transform: translateX(-50%) translateY(0); }
        100% { opacity: 0; transform: translateX(-50%) translateY(-5px); }
      }
      
      /* Make hash text selectable and clickable */
      .hash-text {
        cursor: pointer;
        transition: color 0.2s ease;
        user-select: none;
      }
      
      .hash-text:hover {
        color: #007bff !important;
      }
      
      /* Responsive adjustments for table */
      @media (max-width: 768px) {
        .table-responsive {
          font-size: 14px;
        }
        
        .copy-btn {
          padding: 2px !important;
          min-width: 20px !important;
          min-height: 20px !important;
        }
        
        .register-payment-table th,
        .register-payment-table td {
          padding: 10px 8px !important;
        }
      }
      
      /* Refresh button */
      .refresh-btn {
        transition: all 0.3s ease;
      }
      
      .refresh-btn:hover {
        transform: rotate(180deg);
      }
      
      /* Transaction ID display */
      .transaction-id-badge {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        font-family: monospace;
        font-size: 16px;
        padding: 8px 16px;
        border-radius: 8px;
        margin-left: 10px;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, [sidebarOpen, transactionId, directData]);

  const fetchRegisterPaymentList = async (txId = transactionId) => {
    setIsLoading(true);

    try {
      if (!txId || txId.trim() === '') {
        toast.error("Transaction ID is required");
        setIsLoading(false);
        return;
      }

      const formData = new FormData();
      formData.append('transaction_id', txId);

      const response = await fetch('https://global.thewillowquantum.com/api/get-register-payment-list-by-transaction-id', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
        body: formData
      });
      console.log("response : ", response)
      const result = await response.json();
      console.log("result : ", result)
      if (result?.data[0]?.payment_status === "Approved") {
        toast.success("Payment Approved, logout and login again for dashboard!")
      }
      if (result.status) {
        if (Array.isArray(result.data)) {
          setPaymentList(result.data);
        } else if (result.data) {
          setPaymentList([result.data]);
        } else {
          setPaymentList([]);
        }
      } else {
        toast.error(result.message || "Failed to load payment list");

        if (result.message && (result.message.includes('Session expired') || result.message.includes('Unauthorized'))) {
          setTimeout(() => {
            navigate('/signin');
          }, 1500);
        }
      }
    } catch (error) {
      console.error("Error fetching payment list:", error);
      toast.error("Network error. Please check your connection.");
    } finally {
      setIsLoading(false);
    }
  };


  const handleLogout = async () => {
    try {
      console.log("token : ", token)

      if (token) {
        const response = await fetch('https://global.thewillowquantum.com/api/user/logout', {
          method: 'GET',
          headers: {
            // 'Authorization': token,
            // 'Content-Type': 'application/json'
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json',
            'Content-Type': 'application/x-www-form-urlencoded',
          }
        });

        if (!response.ok) {
          console.warn('Logout API returned non-OK status:', response.status);
        } else {
          const result = await response.json();
          console.log('Logout successful:', result);
          dispatch(logout());

          // Redirect to login
          navigate('/signin');
        }
      }
    } catch (error) {
      console.error('Logout API error:', error);
      // Continue with local logout
    } finally {

    }
  };

  const handleRefresh = () => {
    // 如果正在使用直接数据，刷新时会清除它并调用API
    if (usingDirectData) {
      setUsingDirectData(false);
      if (transactionId) {
        fetchRegisterPaymentList(transactionId);
      }
    } else if (transactionId) {
      fetchRegisterPaymentList(transactionId);
    } else {
      toast.error("No transaction ID available");
    }
  };

  // 获取显示的transaction ID
  const displayTransactionId = usingDirectData ? directData?.transaction_id : transactionId;

  return (
    <div className="main-wrapper dark-theme">


      {/* Page Content */}
      <div className="page-content" style={{ width: "100%" }}>
        {/* Direct Data Banner */}
        {usingDirectData && (
          <div className="row mb-3">
            <div className="col-12">
              <div className="direct-data-banner">
                <div className="d-flex align-items-center">
                  <i className="me-2" data-lucide="check-circle" style={{ color: '#28a745' }}></i>
                  <span className="text-white">Showing your recent payment registration</span>
                  <button
                    className="btn btn-sm btn-outline-light ms-auto"
                    onClick={() => {
                      setUsingDirectData(false);
                      if (transactionId) {
                        fetchRegisterPaymentList(transactionId);
                      }
                    }}
                  >
                    <i className="me-1" data-lucide="refresh-cw" size={14}></i>
                    Get Latest Status
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Page Header */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <div>
                  <h4 className="fw-bold mb-1 text-white">
                    <i className="me-2" data-lucide="file-text"></i>
                    Payment Details
                  </h4>
                  <p className="">
                    {usingDirectData ? 'Your recent payment registration' : 'Transaction details for ID:'}
                  </p>
                </div>
                {displayTransactionId && (
                  <div className="transaction-id-badge ms-3">
                    {displayTransactionId}
                  </div>
                )}
              </div>
              <button
                className="btn btn-primary refresh-btn"
                onClick={handleRefresh}
                disabled={isLoading || !displayTransactionId}
              >
                <i className={`me-1 ${isLoading ? 'spinner-border spinner-border-sm' : ''}`} data-lucide="refresh-cw"></i>
                {isLoading ? 'Loading...' : usingDirectData ? 'Refresh from Server' : 'Refresh'}
              </button>
              <button
                className="btn btn-primary refresh-btn"
                onClick={handleLogout}
                disabled={isLoading || !displayTransactionId}
              >
                <i className={`me-1 ${isLoading ? 'spinner-border spinner-border-sm' : ''}`} data-lucide="refresh-cw"></i>
                {isLoading ? 'Loading...' : usingDirectData ? 'LogOut from Server' : 'LogOut'}
              </button>
            </div>
          </div>
        </div>

        {/* Payment List Card */}
        <div className="row">
          <div className="col-12">
            <div className="card register-payment-card">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h6 className="card-title mb-0 text-white">
                    <i className="me-2" data-lucide="list-checks"></i>
                    Payment Transaction Details
                    {displayTransactionId && (
                      <span className="ms-2 text-muted">(ID: {displayTransactionId})</span>
                    )}
                  </h6>
                  {isLoading && !usingDirectData && (
                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  )}
                  {usingDirectData && (
                    <span className="badge bg-success">
                      <i className="me-1" data-lucide="check"></i>
                      Direct Data
                    </span>
                  )}
                </div>

                <div className="table-responsive">
                  <table className="table register-payment-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Payment Mode</th>
                        <th>Company Receiving Address</th>
                        <th>Amount</th>
                        <th>Sender Transaction Hash ID</th>
                        <th>status</th>
                        <th>comment</th>
                        <th>Created At</th>

                      </tr>
                    </thead>
                    <tbody>
                      {isLoading && !usingDirectData ? (
                        <tr>
                          <td colSpan="10" className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                              <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-2 text-white">Loading payment details for ID: {transactionId}...</p>
                          </td>
                        </tr>
                      ) : paymentList.length > 0 ? (
                        paymentList.map((payment, index) => {
                          const paymentHashId = `payment-${payment.id || index}`;
                          const receiverHashId = `receiver-${payment.id || index}`;

                          return (
                            <tr key={payment.id || index}>
                              <td className="text-white fw-bold">{index + 1}</td>
                              <td>
                                <span className={`badge ${getPaymentModeBadge(payment.payment_mode)}`}>
                                  {payment.payment_mode || 'N/A'}
                                </span>
                              </td>

                              {/* Payment Hash ID with Copy Button */}
                              <td>
                                <div className="d-flex align-items-center gap-2 position-relative">
                                  <small
                                    className="hash-text text-white"
                                    title={payment.payment_hash_id || 'N/A'}
                                    style={{
                                      wordBreak: 'break-all',
                                      flex: 1,
                                      cursor: 'pointer'
                                    }}
                                    onClick={() => copyToClipboard(payment.payment_hash_id, "Payment Hash ID", paymentHashId)}
                                  >
                                    {formatText(payment.payment_hash_id, 12)}
                                  </small>
                                  <div className="position-relative">
                                    <button
                                      className="btn btn-sm btn-outline-secondary p-1 copy-btn"
                                      onClick={() => copyToClipboard(payment.payment_hash_id, "Payment Hash ID", paymentHashId)}
                                      title="Copy Payment Hash ID"
                                      style={{
                                        minWidth: '24px',
                                        minHeight: '24px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}
                                    >
                                      <MdContentCopy size={14} />
                                    </button>
                                    {copiedId === paymentHashId && (
                                      <div className="copy-tooltip">
                                        Copied!
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </td>

                              <td>
                                <strong className="text-success">${parseFloat(payment.payment_amount || 0).toFixed(2)}</strong>
                              </td>

                              {/* Receiver Hash ID with Copy Button */}
                              <td>
                                <div className="d-flex align-items-center gap-2 position-relative">
                                  <small
                                    className="hash-text text-white"
                                    title={payment.payment_receive_hash_id || 'N/A'}
                                    style={{
                                      wordBreak: 'break-all',
                                      flex: 1,
                                      cursor: 'pointer'
                                    }}
                                    onClick={() => copyToClipboard(payment.payment_receive_hash_id, "Receiver Hash ID", receiverHashId)}
                                  >
                                    {formatText(payment.payment_receive_hash_id, 12)}
                                  </small>
                                  <div className="position-relative">
                                    <button
                                      className="btn btn-sm btn-outline-secondary p-1 copy-btn"
                                      onClick={() => copyToClipboard(payment.payment_receive_hash_id, "Receiver Hash ID", receiverHashId)}
                                      title="Copy Receiver Hash ID"
                                      style={{
                                        minWidth: '24px',
                                        minHeight: '24px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}
                                    >
                                      <MdContentCopy size={14} />
                                    </button>
                                    {copiedId === receiverHashId && (
                                      <div className="copy-tooltip">
                                        Copied!
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </td>

                              <td>
                                <small
                                  className="text-white px-2 py-1 rounded d-inline-block"
                                  style={{
                                    backgroundColor: payment.payment_status === 'Approved' ? '#28a745' : '#dc3545',
                                    fontSize: '0.875rem'
                                  }}
                                >
                                  {payment.payment_status}
                                </small>
                              </td>
                              <td>
                                <small className="text-white px-2 py-1 rounded d-inline-block"  style={{
                                    backgroundColor: payment.payment_comment === 'invailed request' ? '#dc3545' : '',
                                    fontSize: '0.875rem'
                                  }}>{payment.payment_comment}</small>
                              </td>
                              <td>
                                <small className="text-white">{formatDate(payment.created_at)}</small>
                              </td>

                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="10" className="text-center py-4">
                            <i data-lucide="search-x" className="icon-lg text-muted mb-2"></i>
                            <h5 className="text-white mb-2">No payment transactions found</h5>
                            <p className="text-muted mb-3">
                              No payment found for transaction ID:
                              <strong className="text-white ms-1">{displayTransactionId || 'Unknown'}</strong>
                            </p>
                            <button
                              className="btn btn-primary"
                              onClick={() => navigate(-1)}
                            >
                              <i className="me-1" data-lucide="arrow-left"></i> Go Back
                            </button>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>

  );
};

export default RegisterPaymentList;